import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  AlertCircle, 
  CheckCircle, 
  Loader, 
  Shield, 
  Info, 
  Upload, 
  RotateCw,
  ChevronRight,
  Clipboard
} from 'lucide-react';
import { Button } from '../ui';
import { 
  parseMultilineInput, 
  importTextFile,
  isValidRedeemCode,
  processRedeemCode,
  extractTokenFromInput,
  saveToLocalStorage,
  loadFromLocalStorage,
  removeFromLocalStorage 
} from '../../utils';
import { STORAGE_KEYS, CONFIG } from '../../constants/config';
import { EXTERNAL_LINKS } from '../../constants';
import { verifyToken, checkRedeemCode } from '../../services';

export const BatchTaskForm = ({ 
  isProcessing, 
  isValidating, 
  onSubmit, 
  addLog, 
  setError, 
  isAllTasksCompleted, 
  initialAccounts = '', 
  initialCodes = '' 
}) => {
  const { t } = useTranslation();
  const [accountsText, setAccountsText] = useState(initialAccounts);
  const [codesText, setCodesText] = useState(initialCodes);
  const [parsedAccounts, setParsedAccounts] = useState([]);
  const [parsedCodes, setParsedCodes] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setLocalError] = useState(null);
  const [isLocalValidating, setIsLocalValidating] = useState(false);
  const [showBatchHelp, setShowBatchHelp] = useState(false);
  const [copyPrefixSuccess, setCopyPrefixSuccess] = useState(false);

  const hasDashFormat = useMemo(() => {
    return parsedAccounts.some(a => a.includes('----'));
  }, [parsedAccounts]);

  const copyAllPrefixes = async () => {
    const prefixes = parsedAccounts.map(a => {
      if (!a.includes('----')) return a;
      const parts = a.split('----');
      return parts.slice(0, -1).join('----');
    }).join('\n');
    try {
      await navigator.clipboard.writeText(prefixes);
      setCopyPrefixSuccess(true);
      setTimeout(() => setCopyPrefixSuccess(false), 1500);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const data = {
      accounts: accountsText,
      codes: codesText
    };
    saveToLocalStorage(STORAGE_KEYS.BATCH_TASK, data);
  }, [accountsText, codesText]);

  useEffect(() => {
    setParsedAccounts(parseMultilineInput(accountsText));
  }, [accountsText]);

  useEffect(() => {
    setParsedCodes(parseMultilineInput(codesText));
  }, [codesText]);

  useEffect(() => {
    if (accountsText || codesText) {
      setValidationResult(null);
    }
  }, [accountsText, codesText]);

  const validateBatch = async () => {
    if (parsedAccounts.length === 0) {
      const errorMsg = t('errors.minOneAccount');
      setLocalError(errorMsg);
      setError(errorMsg);
      return null;
    }

    if (parsedCodes.length === 0) {
      const errorMsg = t('errors.minOneCode');
      setLocalError(errorMsg);
      setError(errorMsg);
      return null;
    }

    if (parsedAccounts.length !== parsedCodes.length) {
      const errorMsg = t('errors.countMismatch', { 
        accounts: parsedAccounts.length, 
        codes: parsedCodes.length 
      });
      setLocalError(errorMsg);
      setError(errorMsg);
      return null;
    }

    setLocalError(null);
    setError(null);

    if (CONFIG.CHECK_DUPLICATE_CODES) {
      const codeMap = new Map();
      const duplicateCodes = [];
      
      parsedCodes.forEach((code, index) => {
        if (codeMap.has(code)) {
          const existingPosition = codeMap.get(code);
          duplicateCodes.push({ 
            code, 
            positions: [existingPosition, index + 1] 
          });
        } else {
          codeMap.set(code, index + 1);
        }
      });

      if (duplicateCodes.length > 0) {
        const duplicateInfo = duplicateCodes.map(dup => 
          t('errors.duplicateCodeDetail', { 
            code: dup.code, 
            positions: dup.positions.join(', ') 
          })
        ).join('；');
        const errorMsg = t('errors.duplicateCodes', { details: duplicateInfo });
        setLocalError(errorMsg);
        setError(errorMsg);
        addLog(errorMsg, 'error');
        return null;
      }
    }

    addLog(`开始并行验证 ${parsedAccounts.length} 个任务...`, 'info');

    try {
      // ---- 阶段 1: 并行验证所有兑换码（相同码去重，只请求一次） ----
      const uniqueCodes = [...new Set(parsedCodes.filter(c => isValidRedeemCode(c)))];
      addLog(`验证 ${uniqueCodes.length} 个不重复兑换码...`, 'info');

      const codeResultMap = {};
      const codePromises = uniqueCodes.map(async (code) => {
        try {
          const codeResult = await checkRedeemCode(code);
          if (!codeResult.success) {
            codeResultMap[code] = { success: false, error: codeResult.message || '兑换码无效' };
          } else {
            codeResultMap[code] = {
              success: true,
              rateLimit: codeResult.rate_limit || null,
              supports_overwrite: codeResult.supports_overwrite || false,
              provider: codeResult.provider || '',
              providerDisplay: codeResult.provider_display || codeResult.provider || '',
            };
          }
        } catch (err) {
          codeResultMap[code] = { success: false, error: `验证异常: ${err.message}` };
        }
      });
      await Promise.all(codePromises);

      const codePassCount = Object.values(codeResultMap).filter(r => r.success).length;
      addLog(`兑换码验证完成: ${codePassCount}/${uniqueCodes.length} 个有效`, codePassCount > 0 ? 'info' : 'error');

      // ---- 阶段 2: 并行验证所有令牌 ----
      addLog(`并行验证 ${parsedAccounts.length} 个令牌...`, 'info');

      const taskResults = new Array(parsedAccounts.length);
      const tokenPromises = parsedAccounts.map(async (account, i) => {
        const code = parsedCodes[i];

        if (!isValidRedeemCode(code)) {
          taskResults[i] = { account, code, isValid: false, error: t('errors.invalidCodeFormat') };
          return;
        }

        const cr = codeResultMap[code];
        if (!cr || !cr.success) {
          taskResults[i] = { account, code, isValid: false, error: cr?.error || '兑换码无效' };
          return;
        }

        const tokenData = extractTokenFromInput(account.trim());
        if (!tokenData.value) {
          taskResults[i] = { account, code, isValid: false, error: t('errors.invalidToken') };
          return;
        }

        try {
          const tokenResult = await verifyToken(tokenData.value);
          if (!tokenResult.success) {
            taskResults[i] = { account, code, isValid: false, error: tokenResult.message || '令牌无效' };
            return;
          }

          let taskIsOverwrite = false;
          const acctInfo = tokenResult.account_info;
          if (acctInfo && !acctInfo.is_free) {
            if (cr.supports_overwrite) {
              taskIsOverwrite = true;
            } else {
              taskResults[i] = {
                account, code, isValid: false,
                error: `账户已有会员，预计于 ${acctInfo.expires_at} 到期`
              };
              return;
            }
          }

          taskResults[i] = {
            account, code, isValid: true,
            email: tokenResult.user_info.email,
            tokenInfo: tokenResult,
            rateLimit: cr.rateLimit,
            provider: cr.provider,
            providerDisplay: cr.providerDisplay,
            isOverwrite: taskIsOverwrite,
            error: null
          };
        } catch (err) {
          taskResults[i] = { account, code, isValid: false, error: `令牌验证异常: ${err.message}` };
        }
      });
      await Promise.all(tokenPromises);

      // ---- 汇总并输出日志 ----
      let validCount = 0;
      taskResults.forEach((r, i) => {
        if (r.isValid) {
          validCount++;
          addLog(`任务 ${i + 1} 验证通过: ${r.email}`, 'success');
        } else {
          addLog(`任务 ${i + 1} 验证失败: ${r.error}`, 'error');
        }
      });

      const result = {
        totalCount: parsedAccounts.length,
        validCount,
        pairs: taskResults
      };

      setValidationResult(result);
      addLog(`验证完成: ${validCount}/${parsedAccounts.length} 个有效`, validCount > 0 ? 'success' : 'error');

      return result;
    } catch (err) {
      const errorMsg = `验证过程中出错: ${err.message}`;
      addLog(errorMsg, 'error');
      setLocalError(errorMsg);
      setError(errorMsg);
      return null;
    }
  };

  const handleValidate = async () => {
    setValidationResult(null);
    setIsLocalValidating(true);
    try {
      const result = await validateBatch();
      if (result) {
        setError(null);
      }
    } finally {
      setIsLocalValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (!validationResult) {
      return;
    }

    const validPairs = validationResult.pairs.filter(pair => pair.isValid);
    if (validPairs.length === 0) {
      const errorMsg = '没有有效的账号和兑换码对';
      setLocalError(errorMsg);
      setError(errorMsg);
      return;
    }

    onSubmit(validPairs);
  };

  const resetBatchForm = () => {
    setAccountsText('');
    setCodesText('');
    setValidationResult(null);
    setLocalError(null);
    setError(null);
    removeFromLocalStorage(STORAGE_KEYS.BATCH_TASK);
  };

  const loadFailedTasks = () => {
    const failedTasks = loadFromLocalStorage(STORAGE_KEYS.FAILED_TASKS, []);
    if (failedTasks.length > 0) {
      const accounts = failedTasks.map(t => t.account).join('\n');
      const codes = failedTasks.map(t => t.code).join('\n');
      setAccountsText(accounts);
      setCodesText(codes);
      addLog(`已加载 ${failedTasks.length} 个失败的任务`, 'info');
    } else {
      addLog('没有失败的任务记录', 'info');
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label htmlFor="accounts" className="block text-sm font-medium text-slate-700 mb-1.5 flex justify-between">
            <span>{t('batch.accountsLabel')}</span>
            <button
              onClick={() => setShowBatchHelp(!showBatchHelp)}
              className="text-indigo-600 hover:text-indigo-700 text-xs flex items-center"
            >
              <Info className="h-3 w-3 mr-1" />
              {t('batch.help.title')}
            </button>
          </label>
          
          {showBatchHelp && (
            <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
              <div className="flex flex-col md:flex-row md:items-start gap-2">
                <div className="flex-1">
                  <p className="text-xs text-indigo-700 mb-1"><strong>{t('common.note')}:</strong>{t('batch.help.note')}</p>
                  <h4 className="text-xs font-medium mb-1">{t('batch.help.supportedFormats')}</h4>
                  <div className="text-xs text-indigo-700 bg-indigo-100 p-2 rounded-lg font-mono overflow-x-auto whitespace-nowrap md:whitespace-normal">
                    <div>1. <code>{t('batch.help.format1')}</code></div>
                    <div>2. <code>{t('batch.help.format2')}</code></div>
                    <div>3. <code>{t('batch.help.format3')}</code></div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mt-2 md:mt-0 md:w-2/5">
                  <p className="text-xs font-medium text-yellow-800">⚠️ {t('batch.help.emptyWarning')}</p>
                  <p className="text-xs text-yellow-700">{t('batch.help.reloginAdvice')}</p>
                </div>
              </div>
            </div>
          )}
          
          <textarea
            id="accounts"
            value={accountsText}
            onChange={(e) => setAccountsText(e.target.value)}
            placeholder={t('batch.accountsPlaceholder')}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 min-h-24 font-mono text-sm transition-all duration-200 overflow-x-auto whitespace-nowrap"
            disabled={isProcessing || isValidating || isLocalValidating}
          />
          <div className="mt-1 text-sm text-slate-500">
            已输入: {parsedAccounts.length} 个账号
          </div>
        </div>

        <div>
          <label htmlFor="codes" className="block text-sm font-medium text-slate-700 mb-1.5">
            {t('batch.codesLabel')}
            <span className="ml-2 text-xs text-slate-500">({t('batch.codesSubtitle')})</span>
          </label>
          <textarea
            id="codes"
            value={codesText}
            onChange={(e) => {
              let newValue = e.target.value;

              if (newValue.includes('http')) {
                newValue = newValue.split('\n')
                  .map(line => {
                    line = line.trim();
                    if (line.includes('?card=')) {
                      const match = line.match(/[?&]card=([A-Za-z0-9]+)/);
                      return match ? match[1].toUpperCase() : line;
                    }
                    return line;
                  })
                  .join('\n');
              }

              setCodesText(newValue.toUpperCase());
            }}
            placeholder={t('batch.codesPlaceholder')}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 min-h-24 font-mono text-sm transition-all duration-200"
            disabled={isProcessing || isValidating || isLocalValidating}
          />
          <div className="mt-1 text-sm text-slate-500">
            已输入: {parsedCodes.length} 个兑换码
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => importTextFile(setAccountsText)}
          disabled={isProcessing || isValidating || isLocalValidating}
        >
          <Upload className="mr-1.5 h-4 w-4" />
          {t('batch.importAccounts')}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => importTextFile(setCodesText)}
          disabled={isProcessing || isValidating || isLocalValidating}
        >
          <Upload className="mr-1.5 h-4 w-4" />
          {t('batch.importCodes')}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={loadFailedTasks}
          disabled={isProcessing || isValidating || isLocalValidating}
          className="border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
        >
          <RotateCw className="mr-1.5 h-4 w-4" />
          {t('batch.loadFailedTasks')}
        </Button>

        {hasDashFormat && parsedAccounts.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={copyAllPrefixes}
            className="border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
          >
            {copyPrefixSuccess ? (
              <><CheckCircle className="mr-1.5 h-4 w-4 text-green-500" />已复制</>
            ) : (
              <><Clipboard className="mr-1.5 h-4 w-4" />复制账号前缀 ({parsedAccounts.length})</>
            )}
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="text-red-500 mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {validationResult && (
        <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <h3 className="text-base font-medium text-indigo-800 mb-2">{t('batch.validationResult')}</h3>
          <div className="flex items-center mb-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full"
                style={{ width: `${(validationResult.validCount / validationResult.totalCount) * 100}%` }}
              ></div>
            </div>
            <span className="ml-3 text-sm font-medium text-indigo-700">
              {`${validationResult?.validCount || 0}/${validationResult?.totalCount || 0} 有效`}
            </span>
          </div>

          <div className="mt-2 max-h-32 overflow-y-auto bg-white rounded border border-gray-200 text-xs">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">{t('batch.tableHeader.index')}</th>
                  <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">{t('batch.tableHeader.account')}</th>
                  <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">{t('batch.tableHeader.code')}</th>
                  <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">{t('batch.tableHeader.status')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {validationResult.pairs.map((pair, index) => (
                  <tr key={index} className={pair.isValid ? '' : 'bg-red-50'}>
                    <td className="px-2 py-1">{index + 1}</td>
                    <td className="px-2 py-1 truncate max-w-[120px]" title={pair.account}>
                      {pair.account}
                    </td>
                    <td className="px-2 py-1 font-mono">{pair.code}</td>
                    <td className="px-2 py-1">
                      {pair.isValid ? (
                        pair.isOverwrite
                          ? <span className="text-orange-600">有效(覆盖)</span>
                          : <span className="text-green-600">{t('batch.statusValid')}</span>
                      ) : (
                        <span className="text-red-600">{pair.error && pair.error.length > 20 ? t('batch.statusInvalid') : pair.error || t('batch.statusInvalid')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        {(!validationResult || (isAllTasksCompleted && (accountsText || codesText))) ? (
          <Button
            onClick={handleValidate}
            disabled={isProcessing || isValidating || isLocalValidating || parsedAccounts.length === 0 || parsedCodes.length === 0}
          >
            {isLocalValidating ? (
              <>
                <Loader className="animate-spin mr-2 h-4 w-4" />
                {t('batch.validating')}
              </>
            ) : (
              <>
                <Shield className="mr-1.5 h-4 w-4" />
                {t('batch.validateTasks')}
              </>
            )}
          </Button>
        ) : (
          !isProcessing && !isAllTasksCompleted ? (
            <Button
              onClick={handleSubmit}
              disabled={validationResult.validCount === 0}
            >
              <>
                {t('batch.startBatchRecharge')}
                <ChevronRight className="ml-1 h-4 w-4" />
              </>
            </Button>
          ) : isProcessing ? (
            <div className="px-4 py-2 text-sm text-gray-600">
              <Loader className="animate-spin inline h-4 w-4 mr-2" />
              {t('batch.tasksProcessing')}
            </div>
          ) : (
            <div className="px-4 py-2 text-sm text-gray-600">
              {t('batch.tasksCompleted')}
            </div>
          )
        )}

        <Button
          variant="secondary"
          onClick={resetBatchForm}
          disabled={isProcessing || isValidating || isLocalValidating}
        >
          {t('form.reset')}
        </Button>
      </div>
    </div>
  );
};